import React, { useState, useEffect } from 'react';
import { crmApi } from '../../services/crmApi';

const ClientEmployeeTab = ({ client }) => {
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employee_month: '',
    employee_count: '',
    notes: ''
  });

  useEffect(() => {
    if (client?.id) {
      fetchEmployeeData();
    }
  }, [client?.id]);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await crmApi.getClientEmployees(client.id, { months: 24 });
      setEmployees(data.employee_history || []);
      setSummary(data.summary || {});
    } catch (err) {
      console.error('Error fetching employee data:', err);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      // Validate form
      if (!formData.employee_month || !formData.employee_count) {
        setError('Month and employee count are required');
        return;
      }

      const count = parseInt(formData.employee_count);
      if (isNaN(count) || count < 0) {
        setError('Employee count must be a valid non-negative number');
        return;
      }

      await crmApi.addEmployeeCount(client.id, {
        employee_month: formData.employee_month,
        employee_count: count,
        notes: formData.notes
      });

      // Reset form and refresh data
      setFormData({ employee_month: '', employee_count: '', notes: '' });
      setIsAddingEmployee(false);
      await fetchEmployeeData();
    } catch (err) {
      console.error('Error adding employee count:', err);
      if (err.response?.status === 409) {
        setError('Employee count already exists for this month. Use edit to update.');
      } else {
        setError(err.response?.data?.error || 'Failed to add employee record');
      }
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      const count = parseInt(formData.employee_count);
      if (isNaN(count) || count < 0) {
        setError('Employee count must be a valid non-negative number');
        return;
      }

      await crmApi.updateEmployeeCount(editingEmployee.id, {
        employee_count: count,
        notes: formData.notes
      });

      // Reset form and refresh data
      setFormData({ employee_month: '', employee_count: '', notes: '' });
      setEditingEmployee(null);
      await fetchEmployeeData();
    } catch (err) {
      console.error('Error updating employee count:', err);
      setError(err.response?.data?.error || 'Failed to update employee record');
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee count record?')) {
      return;
    }

    try {
      setError(null);
      await crmApi.deleteEmployeeCount(employeeId);
      await fetchEmployeeData();
    } catch (err) {
      console.error('Error deleting employee count:', err);
      setError(err.response?.data?.error || 'Failed to delete employee record');
    }
  };

  const startEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      employee_month: employee.employee_month?.substring(0, 7) || '', // Extract YYYY-MM for month input
      employee_count: employee.employee_count || '',
      notes: employee.notes || ''
    });
    setIsAddingEmployee(false);
  };

  const cancelEdit = () => {
    setEditingEmployee(null);
    setIsAddingEmployee(false);
    setFormData({ employee_month: '', employee_count: '', notes: '' });
    setError(null);
  };

  const formatMonth = (dateString) => {
    // dateString is in YYYY-MM-DD format, parse it directly to avoid timezone issues
    const dateParts = dateString.split('-');
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-based
    const date = new Date(year, month, 1); // Create in local timezone
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-8 h-8 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
        <span className="ml-2 text-gray-600">Loading employee data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Employee Summary */}
      {summary && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Employee Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-500">Current Employees</div>
              <div className="text-2xl font-bold text-indigo-600">
                {summary.current_employee_count || 0}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-500">Average Count</div>
              <div className="text-2xl font-bold text-green-600">
                {Math.round(summary.average_employee_count || 0)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-500">Peak Count</div>
              <div className="text-2xl font-bold text-blue-600">
                {summary.max_employee_count || 0}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-500">Months Tracked</div>
              <div className="text-2xl font-bold text-purple-600">
                {summary.months_with_data || 0}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Employee Count History</h3>
        {!isAddingEmployee && !editingEmployee && (
          <button
            onClick={() => setIsAddingEmployee(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Add Employee Count
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">
          {error}
        </div>
      )}

      {/* Add/Edit Employee Form */}
      {(isAddingEmployee || editingEmployee) && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingEmployee ? 'Edit Employee Count' : 'Add Employee Count'}
          </h4>
          <form onSubmit={editingEmployee ? handleEditEmployee : handleAddEmployee}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Month *
                </label>
                <input
                  type="month"
                  value={formData.employee_month}
                  onChange={(e) => setFormData(prev => ({ ...prev, employee_month: e.target.value }))}
                  disabled={!!editingEmployee}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Employee Count *
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.employee_count}
                  onChange={(e) => setFormData(prev => ({ ...prev, employee_count: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div className="mt-4 flex space-x-3">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {editingEmployee ? 'Update' : 'Add'} Employee Count
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Employee Records Table */}
      {employees.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {employees.map((employee) => (
              <li key={employee.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatMonth(employee.employee_month)}
                        </p>
                        <p className="text-lg font-bold text-indigo-600">
                          {employee.employee_count} employees
                        </p>
                        {employee.notes && (
                          <p className="text-sm text-gray-500 mt-1">
                            {employee.notes}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p>Added {new Date(employee.created_at).toLocaleDateString()}</p>
                        {employee.updated_at !== employee.created_at && (
                          <p>Updated {new Date(employee.updated_at).toLocaleDateString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex space-x-2">
                    <button
                      onClick={() => startEdit(employee)}
                      className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteEmployee(employee.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5m-2 3v-3a2 2 0 114 0v3m-6 0a1 1 0 00-1 1v3a1 1 0 001 1h8a1 1 0 001-1v-3a1 1 0 00-1-1m-6 0V9a2 2 0 012-2 2 2 0 012 2v11.1M9 21h4m4 0h4m-4-7a2 2 0 114 0M9 9a2 2 0 012-2h2a2 2 0 012 2v3m4 0V9a2 2 0 012-2h2a2 2 0 012 2v3"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No employee records</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding an employee count record for this client.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientEmployeeTab;
